import { Checkbox } from "@/components/ui/checkbox";
import type { ContenidoDeLaPlantilla } from "@/lib/plantillas";
import {
  alternarConjunto,
  alternarItem,
  arbolDeContenidos,
  estadoDelConjunto,
} from "@/lib/seleccionDeContenidos";
import { cn } from "@/lib/utils";

interface SelectorDeContenidosProps {
  /** Los ítems de la plantilla elegida, en su orden. */
  contenidos: ContenidoDeLaPlantilla[];
  /** ids del maestro que el informe lleva. */
  value: number[];
  onChange: (value: number[]) => void;
}

const PILDORA: Record<ContenidoDeLaPlantilla["obligatoriedad"], string> = {
  obligatorio: "bg-success-soft text-success-soft-foreground",
  condicional: "bg-warning-soft text-warning-soft-foreground",
  opcional: "bg-muted text-ink-muted",
};

const ETIQUETA: Record<ContenidoDeLaPlantilla["obligatoriedad"], string> = {
  obligatorio: "Obligatorio",
  condicional: "Condicional",
  opcional: "Opcional",
};

/**
 * Qué lleva el informe, sobre el árbol de la plantilla elegida.
 *
 * Cada nivel tiene su casilla de tres estados: marcar un grupo o un subgrupo
 * selecciona todo lo suyo, y queda "mixto" cuando hay algunos. Los
 * OBLIGATORIOS vienen marcados y se pueden quitar: la plantilla propone y quien
 * arma el informe decide. Conservan su rótulo para que se vea qué se dejó fuera.
 */
export function SelectorDeContenidos({ contenidos, value, onChange }: SelectorDeContenidosProps) {
  const arbol = arbolDeContenidos(contenidos);
  const elegidos = new Set(value);

  const casilla = (items: ContenidoDeLaPlantilla[], nombre: string) => {
    const estado = estadoDelConjunto(items, value);
    return (
      <Checkbox
        aria-label={nombre}
        checked={estado === "todos" ? true : estado === "algunos" ? "indeterminate" : false}
        onCheckedChange={() =>
          onChange(alternarConjunto(contenidos, value, items, estado !== "todos"))
        }
      />
    );
  };

  return (
    <div className="space-y-3">
      <p className="text-ink-muted text-xs">
        Los contenidos obligatorios vienen marcados según la plantilla. Puedes quitarlos si este
        informe no los necesita.
      </p>

      <div className="bg-card overflow-hidden rounded-xl border">
        {arbol.map((grupo, i) => (
          <div key={grupo.grupo} className={cn(i > 0 && "border-divisor-suave border-t")}>
            <div className="bg-superficie-sutil flex items-center gap-3 px-4 py-2">
              {casilla(grupo.items, `Grupo ${grupo.grupo}`)}
              <span className="text-ink text-sm font-extrabold">{grupo.grupo}</span>
              <span className="text-ink-hint font-mono text-xs font-semibold">
                {grupo.items.filter((c) => elegidos.has(c.maestro_item_id)).length} de{" "}
                {grupo.items.length}
              </span>
            </div>

            {grupo.subgrupos.map((sub) => (
              <div key={sub.subgrupo ?? "—"}>
                {sub.subgrupo !== null ? (
                  <div className="border-divisor-suave flex items-center gap-3 border-t py-1.5 pr-4 pl-9">
                    {casilla(sub.items, `Subgrupo ${sub.subgrupo}`)}
                    <span className="text-ink text-xs font-bold">{sub.subgrupo}</span>
                  </div>
                ) : null}

                {sub.items.map((item) => {
                  const marcado = elegidos.has(item.maestro_item_id);

                  return (
                    <label
                      key={item.maestro_item_id}
                      className={cn(
                        "border-divisor-suave flex items-start gap-3 border-t py-2 pr-4",
                        sub.subgrupo !== null ? "pl-14" : "pl-9",
                        "hover:bg-superficie-sutil cursor-pointer",
                        marcado && "bg-success-soft/40",
                      )}
                    >
                      <Checkbox
                        aria-label={item.nombre}
                        checked={marcado}
                        onCheckedChange={(valor) =>
                          onChange(alternarItem(contenidos, value, item.maestro_item_id, valor === true))
                        }
                        className="mt-0.5"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-ink text-[0.8125rem] font-semibold">
                            {item.nombre}
                          </span>
                          <span
                            className={cn(
                              "rounded-full px-2 py-0.5 text-[0.6875rem] font-bold",
                              PILDORA[item.obligatoriedad],
                            )}
                          >
                            {ETIQUETA[item.obligatoriedad]}
                          </span>
                        </span>
                        {item.obligatoriedad === "condicional" && item.condicion ? (
                          <span className="text-warning-soft-foreground mt-0.5 block text-xs">
                            {item.condicion}
                          </span>
                        ) : null}
                        {item.descripcion ? (
                          <span className="text-ink-muted mt-0.5 line-clamp-2 block text-xs">
                            {item.descripcion}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
